import { Bell, ChevronDown, Menu, Moon, Sun } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useUIStore } from '@/store/uiStore'
import { WebSocketStatus } from '@/components/WebSocketStatus'
import { BackButton } from '@/components/layout/BackButton'
import { AlertSoundButton } from '@/components/layout/AlertSoundButton'
import { LiveIndicator, useLiveStatusToasts } from '@/components/layout/LiveIndicator'  // #328
import { DesktopAlertsButton } from '@/components/layout/DesktopAlertsButton'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'

const ROUTE_KEYS = {
  '/':                   'nav.dashboard',
  '/cameras':            'nav.cameras',
  '/events':             'nav.events',
  '/favourites':         'nav.favourites',
  '/groups':             'nav.groups',
  '/settings':           'nav.settings',
  '/users':              'nav.users',
  '/test-cameras':       'nav.testCameras',
  '/annotations':        'nav.annotations',
  '/camera-logs':        'nav.cameraLogs',
  '/g100-logs':          'nav.g100Logs',
  '/g100-console':       'nav.g100Console',
  '/detection-accuracy': 'nav.detectionAccuracy',
  '/all-cameras':        'nav.allCameras',
  '/sim-mismatches':     'nav.simMismatches',
} as const

// SVG flag components — render identically on all platforms (no emoji variance)
function FlagGB({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
      <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
      <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
      <g clipPath="url(#s)">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
      </g>
    </svg>
  )
}

function FlagJP({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 60 40" xmlns="http://www.w3.org/2000/svg">
      <rect width="60" height="40" fill="#fff"/>
      <circle cx="30" cy="20" r="12" fill="#BC002D"/>
    </svg>
  )
}

function FlagGlobe({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  )
}

// Language display metadata — update when adding a new language
const LANG_DISPLAY: Record<string, { Flag: typeof FlagGB; code: string }> = {
  en: { Flag: FlagGB, code: 'EN' },
  ja: { Flag: FlagJP, code: 'JA' },
}
const DEFAULT_LANG_DISPLAY = LANG_DISPLAY['en']

// Resolve display for current language state
function resolvedLangDisplay(language: string, i18nLang: string): { Flag: typeof FlagGB; code: string } {
  if (language === 'auto') {
    const resolved = i18nLang.split('-')[0]
    return LANG_DISPLAY[resolved] || DEFAULT_LANG_DISPLAY
  }
  return LANG_DISPLAY[language] || DEFAULT_LANG_DISPLAY
}

export function Header() {
  const { t, i18n } = useTranslation('common')
  const { theme, setTheme, language, setLanguage, sidebarOpen, setSidebarOpen } = useUIStore()
  const { pathname } = useLocation()
  const { unreadCount, toggleDrawer } = useNotifications()
  // #328 — "Live updates paused" after 10 s without a live connection, "are back" when it returns
  useLiveStatusToasts()

  const routeKey = ROUTE_KEYS[pathname as keyof typeof ROUTE_KEYS]
  const pageTitle = routeKey ? t(routeKey) : 'DeterCam'
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark')

  const { Flag, code } = resolvedLangDisplay(language, i18n.language)

  const languageOptions: { value: string; label: string; FlagIcon: typeof FlagGB; desc: string }[] = [
    { value: 'auto', label: t('language.auto'), FlagIcon: FlagGlobe, desc: '' },
    { value: 'en', label: 'English', FlagIcon: FlagGB, desc: 'EN' },
    { value: 'ja', label: '日本語', FlagIcon: FlagJP, desc: 'JA' },
  ]

  return (
    <header className="sticky top-0 z-30 flex h-14 sm:h-16 items-center justify-between border-b border-border/60 bg-background px-3 sm:px-4 md:px-6 shadow-[0_1px_0_0_hsl(var(--border)/0.6)]">

      {/* Left — mobile menu + page title */}
      <div className="flex items-center gap-2 sm:gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <Menu className="h-5 w-5" />
        </Button>
        <BackButton />
        <h1 className="text-subheading">{pageTitle}</h1>
      </div>

      {/* Right — status + actions */}
      <div className="flex items-center gap-1 sm:gap-1.5">

        {/* WebSocket status pill — hidden on mobile (saves space for touch targets); #328 — the live status */}
        <div className="hidden sm:block">
          <WebSocketStatus />
        </div>

        {/* Language switcher — pill button with flag + code */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              className="flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-2 py-1 text-xs font-medium text-foreground hover:bg-muted hover:border-border transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title={t('language.label')}
            >
              <Flag className="h-3.5 w-5 rounded-[2px] flex-shrink-0" />
              <span className="hidden sm:inline font-semibold tracking-wide">{code}</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 min-w-[160px] rounded-lg border border-border bg-popover p-1 shadow-elevation-2 animate-in fade-in-0 zoom-in-95"
              sideOffset={5}
              align="end"
            >
              {languageOptions.map((opt) => (
                <DropdownMenu.Item
                  key={opt.value}
                  className={`relative flex cursor-pointer select-none items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none transition-colors hover:bg-muted ${
                    language === opt.value ? 'bg-primary/8 font-semibold text-primary' : 'text-foreground'
                  }`}
                  onSelect={() => setLanguage(opt.value)}
                >
                  <opt.FlagIcon className="h-4 w-5.5 rounded-[2px] flex-shrink-0" />
                  <span className="flex-1">{opt.label}</span>
                  {language === opt.value && (
                    <span className="text-primary text-xs">&#10003;</span>
                  )}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title={theme === 'dark' ? t('header.switchToLight') : t('header.switchToDark')}
        >
          {theme === 'dark'
            ? <Sun className="h-4 w-4" />
            : <Moon className="h-4 w-4" />}
        </Button>

        {/* #328 — the same live status as the pill, as a dot, where the pill is hidden (phones) */}
        <span className="flex items-center px-1 sm:hidden">
          <LiveIndicator />
        </span>

        {/* #295 — shown only while the browser keeps the alert chime blocked */}
        <AlertSoundButton />

        {/* #297 — asks once to turn on desktop alerts; gone after the user answers */}
        <DesktopAlertsButton />

        {/* Notifications bell — opens drawer */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleDrawer}
          className="relative h-8 w-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title={unreadCount > 0 ? t('header.unreadNotifications', { count: unreadCount }) : t('header.notifications')}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground leading-none animate-[badge-pop_0.3s_ease]">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </div>
    </header>
  )
}
